import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import OnboardingDialog from '@/components/OnboardingDialog'
import AdminProfileForm from '@/components/forms/AdminProfileForm'
import ImagePreviewModal from '@/components/ImagePreviewModal'
import { useQuery } from '@tanstack/react-query'
import { useAdminProfile } from '@/hooks/useAdminProfile'
import apiClient from '@/lib/api/client'
import { tenantGetCertification as sdkGetCertification } from '@/lib/api/generated/sdk.gen'
import { useToast } from '@/hooks/use-toast'
import { 
  Building2, 
  User, 
  FileText, 
  Calendar, 
  MapPin, 
  CreditCard, 
  CheckCircle, 
  Clock, 
  XCircle, 
  Edit3,
  Eye,
  Download
} from 'lucide-react'

export default function AccountSettings() {
  const [activeTab, setActiveTab] = useState<'account' | 'cert' | 'admin'>('account')
  const [openCert, setOpenCert] = useState(false)
  const [imagePreview, setImagePreview] = useState<{ url: string; title: string } | null>(null)
  const { toast } = useToast()

  const { data: certData } = useQuery({
    queryKey: ['tenant','certification','account-settings'],
    queryFn: async () => {
      const res: any = await sdkGetCertification({ client: apiClient, responseStyle: 'fields', throwOnError: false })
      const payload: any = (res as any)?.data ?? res
      return payload?.data ?? null
    },
  })

  // 处理图片URL，转换为文件服务接口
  const getImageUrl = (url?: string) => {
    if (!url) return null
    // 如果是blob URL或本地文件路径，转换为文件服务接口
    if (url.startsWith('blob:') || url.startsWith('file:') || !url.startsWith('http')) {
      // 提取文件名，假设URL格式为 "filename.jpg" 或 "path/filename.jpg"
      const fileName = url.split('/').pop() || url
      return `http://localhost:8888/api/v1/files/${fileName}`
    }
    return url
  }

  const { data: adminProfile, isLoading: adminProfileLoading } = useAdminProfile()

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 border-b border-gray-100 bg-white rounded-t-lg p-1">
        <button 
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-md transition-all duration-200 ${
            activeTab === 'account' 
              ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`} 
          onClick={() => setActiveTab('account')}
        >
          <User className="w-4 h-4" />
          账户信息
        </button>
        <button 
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-md transition-all duration-200 ${
            activeTab === 'cert' 
              ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`} 
          onClick={() => setActiveTab('cert')}
        >
          <FileText className="w-4 h-4" />
          资质认证
        </button>
        <button 
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-md transition-all duration-200 ${
            activeTab === 'admin' 
              ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`} 
          onClick={() => setActiveTab('admin')}
        >
          <Building2 className="w-4 h-4" />
          管理员配置
        </button>
      </div>

      {activeTab === 'account' && (
        <Card>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>公司名称</Label>
              <Input placeholder="北京xxx有限公司" />
            </div>
            <div className="space-y-2">
              <Label>联系人</Label>
              <Input placeholder="张三" />
            </div>
            <div className="space-y-2">
              <Label>密保手机</Label>
              <Input placeholder="13800000000" />
            </div>
            <div className="space-y-2">
              <Label>紧急联系人</Label>
              <Input placeholder="李四" />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <Button disabled>保存（占位）</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'cert' && (
        <div className="space-y-6">
          {/* 操作栏 */}
          <Card className={`border-0 shadow-sm ${
            certData?.certificationStatus === 'VERIFIED' 
              ? 'bg-gradient-to-r from-green-50 to-emerald-50' 
              : 'bg-gradient-to-r from-blue-50 to-indigo-50'
          }`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    certData?.certificationStatus === 'VERIFIED' 
                      ? 'bg-green-100' 
                      : 'bg-blue-100'
                  }`}>
                    {certData?.certificationStatus === 'VERIFIED' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <FileText className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {certData?.certificationStatus === 'VERIFIED' ? '企业认证已完成' : '企业认证'}
                    </div>
                    <div className="text-xs text-gray-600">
                      {certData?.certificationStatus === 'VERIFIED' 
                        ? '您的企业已成功通过认证，可以享受全部功能' 
                        : '提交企业认证以解锁全部能力'
                      }
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={() => setOpenCert(true)}
                  className={`px-6 py-2 rounded-lg shadow-sm ${
                    certData?.certificationStatus === 'VERIFIED'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  {certData?.certificationStatus === 'VERIFIED' ? '更新认证' : '发起认证'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* 认证状态概览 */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">认证状态</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
                  <div className="flex items-center gap-3 mb-2">
                    {certData?.certificationStatus === 'VERIFIED' && (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                    {certData?.certificationStatus === 'PENDING' && (
                      <Clock className="w-5 h-5 text-yellow-600" />
                    )}
                    {certData?.certificationStatus === 'REJECTED' && (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    {!certData?.certificationStatus && (
                      <FileText className="w-5 h-5 text-gray-600" />
                    )}
                    <Badge 
                      variant={
                        certData?.certificationStatus === 'VERIFIED' ? 'default' :
                        certData?.certificationStatus === 'PENDING' ? 'secondary' :
                        certData?.certificationStatus === 'REJECTED' ? 'destructive' : 'outline'
                      }
                      className="text-xs"
                    >
                      {certData?.certificationStatus === 'VERIFIED' && '已认证'}
                      {certData?.certificationStatus === 'PENDING' && '审核中'}
                      {certData?.certificationStatus === 'REJECTED' && '已驳回'}
                      {!certData?.certificationStatus && '未提交'}
                    </Badge>
                  </div>
                  <div className="text-xs text-gray-500">认证状态</div>
                </div>
                
                <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    <div className="text-xs text-gray-500">认证时间</div>
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    {certData?.verificationTime ? new Date(certData.verificationTime).toLocaleString('zh-CN') : '-'}
                  </div>
                </div>
                
                <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-purple-600" />
                    <div className="text-xs text-gray-500">更新时间</div>
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    {certData?.updatedAt ? new Date(certData.updatedAt).toLocaleString('zh-CN') : '-'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 企业基本信息 */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">企业基本信息</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4 text-gray-500" />
                      <div className="text-xs text-gray-500 font-medium">企业名称</div>
                    </div>
                    <div className="text-sm font-medium text-gray-900">{certData?.companyName || '-'}</div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <div className="text-xs text-gray-500 font-medium">统一社会信用代码</div>
                    </div>
                    <div className="text-sm font-medium text-gray-900">{certData?.businessLicense || '-'}</div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CreditCard className="w-4 h-4 text-gray-500" />
                      <div className="text-xs text-gray-500 font-medium">注册资本</div>
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {certData?.registeredCapital ? `${certData.registeredCapital}万元` : '-'}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <div className="text-xs text-gray-500 font-medium">营业执照有效期</div>
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {certData?.licenseExpireDate ? new Date(certData.licenseExpireDate).toLocaleDateString('zh-CN') : '-'}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg md:col-span-2">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <div className="text-xs text-gray-500 font-medium">经营范围</div>
                    </div>
                    <div className="text-sm font-medium text-gray-900">{certData?.businessScope || '-'}</div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <div className="text-xs text-gray-500 font-medium">注册地址</div>
                    </div>
                    <div className="text-sm font-medium text-gray-900">{certData?.registeredAddress || '-'}</div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <div className="text-xs text-gray-500 font-medium">经营地址</div>
                    </div>
                    <div className="text-sm font-medium text-gray-900">{certData?.businessAddress || '-'}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 法人信息 */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">法人信息</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <div className="text-xs text-gray-500 font-medium">法定代表人</div>
                  </div>
                  <div className="text-sm font-medium text-gray-900">{certData?.legalPerson || '-'}</div>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <div className="text-xs text-gray-500 font-medium">营业执照</div>
                  </div>
                  <div className="text-sm font-medium">
                    {certData?.licenseUrl ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setImagePreview({ url: getImageUrl(certData.licenseUrl)!, title: '营业执照' })}
                        className="h-auto p-0 text-blue-600 hover:text-blue-700 hover:bg-transparent"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        查看图片
                      </Button>
                    ) : '-'}
                  </div>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-gray-500" />
                    <div className="text-xs text-gray-500 font-medium">法人身份证正面</div>
                  </div>
                  <div className="text-sm font-medium">
                    {certData?.idCardFrontUrl ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setImagePreview({ url: getImageUrl(certData.idCardFrontUrl)!, title: '法人身份证正面' })}
                        className="h-auto p-0 text-blue-600 hover:text-blue-700 hover:bg-transparent"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        查看图片
                      </Button>
                    ) : '-'}
                  </div>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-gray-500" />
                    <div className="text-xs text-gray-500 font-medium">法人身份证背面</div>
                  </div>
                  <div className="text-sm font-medium">
                    {certData?.idCardBackUrl ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setImagePreview({ url: getImageUrl(certData.idCardBackUrl)!, title: '法人身份证背面' })}
                        className="h-auto p-0 text-blue-600 hover:text-blue-700 hover:bg-transparent"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        查看图片
                      </Button>
                    ) : '-'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 审核信息 */}
          {certData?.verificationComment && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <FileText className="w-4 h-4 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">审核信息</h3>
                </div>
                <div className="p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-100">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-orange-600" />
                    <div className="text-xs text-orange-700 font-medium">审核意见</div>
                  </div>
                  <div className="text-sm font-medium text-gray-900 leading-relaxed">
                    {certData.verificationComment}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'admin' && (
        <Card>
          <CardContent className="p-6">
            {adminProfileLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-sm text-muted-foreground">加载中...</div>
              </div>
            ) : (
              <AdminProfileForm 
                adminProfile={adminProfile}
                onSuccess={() => {
                  toast({
                    title: '保存成功',
                    description: '管理员配置已更新',
                  })
                }}
              />
            )}
          </CardContent>
        </Card>
      )}

      <OnboardingDialog open={openCert} onOpenChange={setOpenCert} />
      
      {/* 图片预览Modal */}
      <ImagePreviewModal
        open={!!imagePreview}
        onOpenChange={(open) => !open && setImagePreview(null)}
        imageUrl={imagePreview?.url}
        title={imagePreview?.title}
      />
    </div>
  )
}


